import { Alert, Box, Card, IconButton, Modal, Snackbar, Stack, Typography } from '@mui/material';
import close2 from '../../../icons/close2.svg';
import { ApplicationDecisionFileExportDto, ApplicationDecisionFileTypeEnum, ApplicationStatus, ApplicationSummaryDto, SendApplicationStatusNotificationDto } from '@inosio/payments-sdk';
import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import UncheckedIcon from '../../../icons/UncheckedIcon.svg';
import CheckedIcon from '../../../icons/CheckedIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { downloadApplicationDecisionPdf, selectApplicationsState, sendApplicationDecisionNotification } from '../../../resui/applications/sagas/pm/applicationsSlice';
import { LoadingButton } from '@mui/lab';

export interface ApplicantNotificationModalProps {
  open: boolean;
  onClose: () => void;
  applicationDecision: ApplicationStatus | null;
  application: ApplicationSummaryDto | undefined;
}

const useStyles = makeStyles((theme) => ({
  bodyText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
  },
}));

export default function ApplicantNotificationModal(props: ApplicantNotificationModalProps) {
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    height: '523px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    borderRadius: '24px',
    outline: 'none',
  };
  const outlinedButtonStyle = {
    color: '#39A2DB',
    border: '2px solid #39A2DB',
    boxShadow: 'none',
    borderRadius: '24px',
    fontSize: '14px',
    fontStyle: 'normal',
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: '#39A2DB',
      color: 'white',
      border: '2px solid #39A2DB',
    },
    width: '150px',
    height: '40px',
  };
  const classes = useStyles();
  const dispatch = useDispatch();

  //selectors
  const applicationState = useSelector(selectApplicationsState);
  const {
    isSendingApplicationDecisionNotification,
    isSendingApplicationDecisionNotificationFailure,
    isSendingApplicationDecisionNotificationSuccess,
    isDownloadingApplicationDecisionPdf,
    isDownloadingApplicationDecisionPdfFailure,
    isDownloadingApplicationDecisionPdfSuccess,
  } = applicationState;

  //local state
  const [recipientUuids, setRecipientUuids] = useState<Array<string>>([]);
  const [errorSnackBarOpen, setErrorSnackBarOpen] = useState(false);
  const [localError, setLocalError] = useState('');
  const [checkDownloadLetterBox, setCheckDownloadLetterBox] = useState(false);
  const [checkEmailLetterBox, setCheckEmailLetterBox] = useState(false);

  //handlers
  const handleCloseErrorSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorSnackBarOpen(false);
  };

  const handleClose = () => {
    props.onClose();
    setRecipientUuids([]);
    setCheckDownloadLetterBox(false);
    setCheckEmailLetterBox(false);
  };

  const updateRecipientList = (applicantUuid: string) => {
    if (applicantUuid) {
      if (recipientUuids.some((recipientUuid) => recipientUuid === applicantUuid)) {
        setRecipientUuids((recipientUuids) => recipientUuids.filter((recipientUuid) => recipientUuid !== applicantUuid));
      } else {
        setRecipientUuids((recipientUuids) => [...recipientUuids, applicantUuid]);
      }
    }
  };

  const handleSendDecisionNotification = () => {
    if (recipientUuids.length === 0) {
      setLocalError('Please select at least one applicant');
      setErrorSnackBarOpen(true);
    } else {
      dispatch({
        type: sendApplicationDecisionNotification.type,
        payload: {
          applicationUuid: props.application?.Uuid,
          sendApplicationStatusNotificationDto: {
            RecipientUuids: recipientUuids,
          } as SendApplicationStatusNotificationDto,
        },
      });
    }
  };

  const handleDownloadDecisionFile = () => {
    if (recipientUuids.length === 0) {
      setLocalError('Please select at least one applicant');
      setErrorSnackBarOpen(true);
    } else {
      recipientUuids.forEach((applicantUuid) => {
        dispatch({
          type: downloadApplicationDecisionPdf.type,
          payload: {
            applicationUuid: props.application?.Uuid,
            applicationDecisionFileExportDto: {
              ApplicantUuid: applicantUuid,
              Type: ApplicationDecisionFileTypeEnum.DenialLetter,
            } as ApplicationDecisionFileExportDto,
          },
        });
      });
    }
  };

  const handleSubmit = () => {
    if (!checkDownloadLetterBox && !checkEmailLetterBox) {
      setLocalError('Please select at least one option');
      setErrorSnackBarOpen(true);
    }
    if (checkDownloadLetterBox) handleDownloadDecisionFile();
    if (checkEmailLetterBox) handleSendDecisionNotification();
  };

  //useffects
  useEffect(() => {
    if (isDownloadingApplicationDecisionPdfFailure || isSendingApplicationDecisionNotificationFailure) {
      setLocalError('Apologies, something went wrong. Please try again later or contact support. Thank you.');
      setErrorSnackBarOpen(true);
    }
  }, [isDownloadingApplicationDecisionPdfFailure, isSendingApplicationDecisionNotificationFailure]);

  useEffect(() => {
    if (isSendingApplicationDecisionNotificationSuccess || isDownloadingApplicationDecisionPdfSuccess) {
      handleClose();
    }
  }, [isSendingApplicationDecisionNotificationSuccess, isDownloadingApplicationDecisionPdfSuccess]);

  return (
    <Box>
      <Modal
        open={props.open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        BackdropProps={{
          invisible: false,
        }}
      >
        <Card sx={style}>
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ position: 'relative', mt: 1 }}>
            <Typography
              sx={{
                fontFamily: '"Work Sans",Sans serif',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '22px',
                color: '#14142B',
                marginLeft: '0 !important',
              }}
            >
              Applicant Notification
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Close modal"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 0,
              }}
            >
              <img src={close2} alt="close" />
            </IconButton>
          </Stack>
          <Typography className={classes.bodyText} sx={{ mt: 4, mb: 2, pl: 2 }}>
            Select applicants to notify:
          </Typography>
          <Box
            p={2}
            pl={4}
            sx={{
              overflow: 'auto',
              height: '160px',
              '&::-webkit-scrollbar': {
                width: 7,
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                borderRadius: '24px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'darkgrey',
                borderRadius: '24px',
              },
            }}
          >
            {props.application?.Applicants.map((applicant, index) => {
              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 2, cursor: 'pointer' }} onClick={() => updateRecipientList(applicant?.Uuid ?? '')}>
                  <Box sx={{ width: 20, height: 17 }}>
                    <img src={recipientUuids.some((recipientUuid) => recipientUuid === applicant?.Uuid) ? CheckedIcon : UncheckedIcon} alt="checked" />
                  </Box>
                  <Typography className={classes.bodyText} sx={{ ml: 4 }}>
                    {`${applicant?.FirstName ?? ''} ${applicant?.LastName ?? ''}`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {props.applicationDecision === ApplicationStatus.Rejected && (
            <Box>
              <Typography className={classes.bodyText} sx={{ mt: 4, mb: 4, pl: 2 }}>
                {`Please select option(s):`}
              </Typography>
              <Box
                pl={4}
                sx={{ display: 'flex', alignItems: 'center', mt: 2, cursor: 'pointer' }}
                onClick={() => {
                  setCheckDownloadLetterBox(!checkDownloadLetterBox);
                }}
              >
                <Box sx={{ width: 20, height: 17 }}>
                  <img src={checkDownloadLetterBox ? CheckedIcon : UncheckedIcon} alt="checked" />
                </Box>
                <Typography className={classes.bodyText} sx={{ ml: 4 }}>
                  Download Letter
                </Typography>
              </Box>
              <Box
                pl={4}
                sx={{ display: 'flex', alignItems: 'center', mt: 2, cursor: 'pointer' }}
                onClick={() => {
                  setCheckEmailLetterBox(!checkEmailLetterBox);
                }}
              >
                <Box sx={{ width: 20, height: 17 }}>
                  <img src={checkEmailLetterBox ? CheckedIcon : UncheckedIcon} alt="checked" />
                </Box>
                <Typography className={classes.bodyText} sx={{ ml: 4 }}>
                  {`Email Letter to Applicant(s)`}
                </Typography>
              </Box>
            </Box>
          )}

          <Box mt={props.applicationDecision === ApplicationStatus.Rejected ? 4 : 20} p={2} sx={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
            {props.applicationDecision === ApplicationStatus.Approved && (
              <Stack spacing={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingButton
                  loading={isSendingApplicationDecisionNotification}
                  disabled={isSendingApplicationDecisionNotification}
                  variant="outlined"
                  sx={outlinedButtonStyle}
                  onClick={handleSendDecisionNotification}
                >
                  Send
                </LoadingButton>
              </Stack>
            )}
            {props.applicationDecision === ApplicationStatus.Rejected && (
              <Stack spacing={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingButton
                  loading={isSendingApplicationDecisionNotification || isDownloadingApplicationDecisionPdf}
                  disabled={isSendingApplicationDecisionNotification || isDownloadingApplicationDecisionPdf}
                  variant="outlined"
                  sx={outlinedButtonStyle}
                  onClick={handleSubmit}
                >
                  Submit
                </LoadingButton>
              </Stack>
            )}
          </Box>
        </Card>
      </Modal>
      {/* error alert both for validations and failed api calls */}
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={errorSnackBarOpen} autoHideDuration={4000} onClose={handleCloseErrorSnackbar}>
        <Alert onClose={handleCloseErrorSnackbar} severity="error">
          {localError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
